import { ReactNode, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, isValid, parse } from 'date-fns';
import { ru, uk } from 'date-fns/locale';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label as LabelUI } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { cn } from '@/lib/utils';

import { DISPLAY_MONTH_FORMAT, MONTH_FORMAT } from '@/constants/dates';

function parseMonthPickerValue(monthValue: string): Date | undefined {
  let parsed: Date;
  try {
    parsed = parse(`${monthValue}-01`, 'yyyy-MM-dd', new Date());
  } catch {
    return undefined;
  }
  if (!isValid(parsed)) {
    return undefined;
  }
  return parsed;
}

type MonthPickerProps = {
  value?: string;
  onChange?: (value: string) => void;
  label?: ReactNode;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-invalid'?: boolean;
};

const getLocale = (lang: string) => {
  if (lang === 'uk') return uk;
  if (lang === 'ru') return ru;
  return undefined;
};

const MonthPicker = (props: MonthPickerProps) => {
  const {
    value,
    onChange,
    label,
    disabled,
    className,
    id,
    'aria-invalid': ariaInvalid,
  } = props;

  const { i18n } = useTranslation();
  const locale = getLocale(i18n.language);

  const generatedId = useId();
  const pickerId = id ?? generatedId;

  const [isOpen, setIsOpen] = useState(false);

  const parsedDate = useMemo(() => {
    if (!value) return undefined;
    return parseMonthPickerValue(value);
  }, [value]);

  const [viewYear, setViewYear] = useState(() => {
    return parsedDate ? parsedDate.getFullYear() : new Date().getFullYear();
  });

  const displayValue = parsedDate
    ? format(parsedDate, DISPLAY_MONTH_FORMAT, { locale })
    : '';

  const isFloating = isOpen || Boolean(value);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setViewYear(parsedDate?.getFullYear() ?? new Date().getFullYear());
    }
    setIsOpen(open);
  };

  const handleMonthSelect = (monthIndex: number) => {
    const date = new Date(viewYear, monthIndex, 1);
    const formattedValue = format(date, MONTH_FORMAT);
    onChange?.(formattedValue);
    setIsOpen(false);
  };

  const months = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const date = new Date(viewYear, i, 1);
      const name = format(date, 'LLLL', { locale });

      return {
        index: i,
        name: name.charAt(0).toUpperCase() + name.slice(1),
      };
    });
  }, [viewYear, locale]);

  const selectedMonth = parsedDate?.getMonth();
  const selectedYear = parsedDate?.getFullYear();

  return (
    <div className={cn('relative', className)}>
      <Popover
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <PopoverTrigger asChild>
          <Button
            id={pickerId}
            variant="outline"
            className={cn(
              'h-10! w-full justify-start border-2 border-transparent! bg-transparent text-left font-normal shadow-none ring-0 outline-none hover:bg-transparent',
              'focus-visible:border-transparent! focus-visible:ring-0 aria-invalid:border-transparent! aria-invalid:ring-0',
              'dark:bg-card dark:hover:bg-card dark:border-transparent!',
              !value && 'text-muted-foreground',
            )}
            type="button"
            disabled={disabled}
            aria-invalid={ariaInvalid}
          >
            <CalendarIcon className="mr-2 size-4" />
            {displayValue || <span>&nbsp;</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-3"
          align="start"
        >
          <div className="mb-3 flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={() => setViewYear((y) => y - 1)}
              type="button"
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span className="text-sm font-medium">{viewYear}</span>
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={() => setViewYear((y) => y + 1)}
              type="button"
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {months.map((month) => {
              const isSelected =
                selectedMonth === month.index && selectedYear === viewYear;
              return (
                <Button
                  key={month.index}
                  variant={isSelected ? 'default' : 'ghost'}
                  size="sm"
                  className="h-8"
                  onClick={() => handleMonthSelect(month.index)}
                  type="button"
                >
                  {month.name}
                </Button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      {label && (
        <>
          <fieldset
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-0 top-[-5px] z-1 overflow-hidden rounded-md border-2 border-solid bg-transparent px-[9px] transition-colors',
              isOpen && !ariaInvalid && 'border-primary',
              !isOpen && !ariaInvalid && 'border-input',
              ariaInvalid && 'border-destructive',
            )}
          >
            <legend
              className={cn(
                'invisible block h-[11px] w-auto overflow-hidden p-0 text-xs whitespace-nowrap',
                isFloating
                  ? 'max-w-full transition-[max-width] delay-50 duration-100 ease-out'
                  : 'max-w-[0.01px] transition-[max-width] duration-50 ease-out',
              )}
            >
              <span className="visible inline-block px-1 opacity-0">
                {label}
              </span>
            </legend>
          </fieldset>
          <LabelUI
            htmlFor={pickerId}
            className={cn(
              'bg-card text-muted-foreground pointer-events-none absolute z-2 gap-1 rounded-xs px-1 transition-all duration-200 ease-out',
              isFloating
                ? 'top-0 left-[13px] -translate-y-1/2 text-xs'
                : 'top-1/2 left-[37px] -translate-y-1/2 text-sm',
              ariaInvalid ? 'text-destructive' : isOpen && 'text-primary',
            )}
          >
            {label}
          </LabelUI>
        </>
      )}
    </div>
  );
};

export { MonthPicker };
export type { MonthPickerProps };
